import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import { switchHome } from '../../utils/api';

const PRIMARY_LINKS = [
  { to: '/', label: 'Home', icon: '⌂' },
  { to: '/tasks', label: 'Tasks', icon: '✓' },
  { to: '/projects', label: 'Projects', icon: '◈' },
  { to: '/calendar', label: 'Calendar', icon: '◻' },
];

const MORE_LINKS = [
  { to: '/rooms', label: 'Rooms' },
  { to: '/appliances', label: 'Appliances' },
  { to: '/consumables', label: 'Supplies' },
  { to: '/shopping', label: 'Shopping' },
  { to: '/contacts', label: 'Contacts' },
  { to: '/expenses', label: 'Expenses' },
  { to: '/tools', label: 'Tools' },
  { to: '/history', label: 'History' },
  { to: '/feature-requests', label: 'Feature Requests' },
];

const Navbar = ({ user, homes, onLogout, onHomeSwitch }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [desktopMoreOpen, setDesktopMoreOpen] = useState(false);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [homeOpen, setHomeOpen] = useState(false);
  const desktopMoreRef = useRef(null);
  const userMenuRef = useRef(null);
  const homeMenuRef = useRef(null);

  const activeHome = homes?.find((h) => h._id?.toString() === user?.activeHomeId?.toString());

  useEffect(() => { setDesktopMoreOpen(false); setMobileSheetOpen(false); setUserOpen(false); setHomeOpen(false); }, [pathname]);

  useEffect(() => {
    const handler = (e) => {
      if (desktopMoreRef.current && !desktopMoreRef.current.contains(e.target)) setDesktopMoreOpen(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserOpen(false);
      if (homeMenuRef.current && !homeMenuRef.current.contains(e.target)) setHomeOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Cmd/Ctrl+K opens search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        navigate('/search');
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate]);

  const isActive = (path) => pathname.startsWith(path) && (path !== '/' || pathname === '/');
  const linkClass = (path) => `nav-link${isActive(path) ? ' active' : ''}`;
  const moreActive = MORE_LINKS.some(({ to }) => isActive(to));
  const moreActiveOrOpen = moreActive || desktopMoreOpen;

  const handleLogout = async () => {
    await fetch('/api/auth/logout', { method: 'POST' });
    onLogout();
    navigate('/login');
  };

  const handleSwitchHome = async (homeId) => {
    await switchHome(homeId);
    onHomeSwitch();
    window.location.href = '/';
  };

  return (
    <>
      <nav className="navbar">
        {/* Home switcher brand */}
        {homes?.length > 1 ? (
          <div className="nav-home-switcher" ref={homeMenuRef}>
            <button
              className="navbar-brand navbar-brand-btn"
              onClick={() => setHomeOpen((v) => !v)}
              title="Switch property"
            >
              🏠 {activeHome?.name || 'Home Maintenance'} ▾
            </button>
            {homeOpen && (
              <div className="nav-dropdown nav-home-dropdown">
                {homes.map((h) => (
                  <button
                    key={h._id}
                    className={`nav-dropdown-item${h._id?.toString() === user?.activeHomeId?.toString() ? ' active' : ''}`}
                    onClick={() => handleSwitchHome(h._id)}
                  >
                    {h._id?.toString() === user?.activeHomeId?.toString() ? '✓ ' : '   '}{h.name}
                  </button>
                ))}
                <div className="nav-dropdown-divider" />
                <Link to="/homes" className="nav-dropdown-item">Manage Properties</Link>
              </div>
            )}
          </div>
        ) : (
          <div className="navbar-brand">
            <span>🏠</span> {activeHome?.name || 'Home Maintenance'}
          </div>
        )}

        {/* Desktop nav */}
        <ul className="nav-links nav-links-desktop">
          {PRIMARY_LINKS.map(({ to, label }) => (
            <li key={to}><Link to={to} className={linkClass(to)}>{label}</Link></li>
          ))}
          <li ref={desktopMoreRef} style={{ position: 'relative' }}>
            <button
              className={`nav-link nav-more-btn${moreActiveOrOpen ? ' active' : ''}`}
              onClick={() => setDesktopMoreOpen((v) => !v)}
            >
              More ▾
            </button>
            {desktopMoreOpen && (
              <div className="nav-dropdown">
                {MORE_LINKS.map(({ to, label }) => (
                  <Link key={to} to={to} className={`nav-dropdown-item${isActive(to) ? ' active' : ''}`}>
                    {label}
                  </Link>
                ))}
              </div>
            )}
          </li>
        </ul>

        {/* Right-side actions */}
        <div className="navbar-actions">
          <Link to="/search" className="notif-btn" title="Search (⌘K / Ctrl+K)">🔍</Link>
          <NotificationBell />
          {user && (
            <div className="nav-user-menu" ref={userMenuRef}>
              <button
                className="nav-avatar-btn"
                onClick={() => setUserOpen((v) => !v)}
                title={user.name}
              >
                {user.photo
                  ? <img src={user.photo} className="nav-user-photo" alt={user.name} referrerPolicy="no-referrer" />
                  : <span className="nav-user-initials">{user.name?.[0]?.toUpperCase()}</span>
                }
              </button>
              {userOpen && (
                <div className="nav-user-dropdown">
                  <div className="nav-user-info">
                    <span className="nav-user-name">{user.name}</span>
                    <span className="nav-user-email">{user.email}</span>
                  </div>
                  <div className="nav-user-divider" />
                  <Link to="/home-profile" className="nav-user-dropdown-item">Home Profile</Link>
                  <Link to="/homes" className="nav-user-dropdown-item">Manage Properties</Link>
                  {user.isAdmin && <Link to="/admin" className="nav-user-dropdown-item">Admin</Link>}
                  <button className="nav-user-dropdown-item nav-user-signout" onClick={handleLogout}>
                    Sign out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Mobile bottom tab bar */}
      <nav className="bottom-nav">
        {PRIMARY_LINKS.map(({ to, label, icon }) => (
          <Link key={to} to={to} className={`bottom-nav-item${isActive(to) ? ' active' : ''}`}>
            <span className="bottom-nav-icon">{icon}</span>
            <span className="bottom-nav-label">{label}</span>
          </Link>
        ))}
        <button
          className={`bottom-nav-item${moreActive ? ' active' : ''}`}
          onClick={() => setMobileSheetOpen((v) => !v)}
        >
          <span className="bottom-nav-icon">···</span>
          <span className="bottom-nav-label">More</span>
        </button>
      </nav>

      {/* Mobile More sheet */}
      {mobileSheetOpen && (
        <div className="more-sheet-overlay" onClick={() => setMobileSheetOpen(false)}>
          <div className="more-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="more-sheet-handle" />
            {homes?.length > 1 && (
              <>
                <p style={{ padding: '0.5rem 1.25rem 0.25rem', fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Switch Property
                </p>
                {homes.map((h) => (
                  <button
                    key={h._id}
                    className="more-sheet-item"
                    onClick={() => handleSwitchHome(h._id)}
                  >
                    {h._id?.toString() === user?.activeHomeId?.toString() ? '✓ ' : ''}{h.name}
                  </button>
                ))}
                <div style={{ borderTop: '1px solid var(--border)', margin: '0.5rem 0' }} />
              </>
            )}
            {MORE_LINKS.map(({ to, label }) => (
              <Link key={to} to={to} className={`more-sheet-item${isActive(to) ? ' active' : ''}`}>
                {label}
              </Link>
            ))}
            <Link to="/home-profile" className="more-sheet-item">Home Profile</Link>
            <Link to="/homes" className="more-sheet-item">Manage Properties</Link>
            {user?.isAdmin && <Link to="/admin" className="more-sheet-item">Admin</Link>}
            {user && (
              <button className="more-sheet-item more-sheet-signout" onClick={handleLogout}>
                Sign out
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
